<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import InitialsAvatar from '@/components/general/InitialsAvatar.vue';
import { useRouter } from 'vue-router';
import useAuthService from '@/composables/apiServices/useAuthService';
import { ROUTE_NAMES } from '@/router/conf';
import { useDisplay } from 'vuetify';
import useAvatarService from '@/composables/apiServices/useAvatarService';
import { useAuthChannel } from '@/composables/useAuthChannel';
import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import useUserService from '@/composables/apiServices/useUserService';

const isOpen = ref(false);
const showLogoutList = ref(false);

const { t } = useI18n();
const router = useRouter();
const { mobile } = useDisplay();
const logoutListIcon = computed(() => (showLogoutList.value ? 'mdi-arrow-up' : 'mdi-arrow-down'));

const { userIndex } = useAuthenticatedQuery();
const { getAccounts, logout, logoutAllDeviceUsers, mePolling } = useAuthService();
const { getAvatar } = useAvatarService();
const { attributes } = useUserService();
const { mutateAsync: mutateLogout } = logout();
const { mutateAsync: mutateLogoutAll } = logoutAllDeviceUsers();
const { data: accountsData, suspense: loadAccounts } = getAccounts();
const { data: meData, suspense: meSuspense } = mePolling();
const { data: avatarData } = getAvatar();
const { data: attributesData } = attributes();
const { postAfterLoggedOut } = useAuthChannel();
await Promise.all([loadAccounts(), meSuspense()]);

const accounts = computed(() => accountsData.value ?? []);
const currentAccount = computed(() => meData.value);

const filteredAccounts = computed(() =>
  accounts.value.filter((account) => account.email !== currentAccount.value?.email)
);

const redirectToLogin = async (newTab = false, forcePasswordRedirect = true) => {
  const loginUrl = router.resolve({
    name: ROUTE_NAMES.AUTH_LOGIN_PASSWORD,
    query: { reason: 'addNewAccount' }
  }).href;

  if (newTab) {
    window.open(loginUrl, '_blank');
  } else {
    // If there were more than one user logged in before redirect to user select else to
    // password view
    if (accounts.value.length > 1 && !forcePasswordRedirect) {
      await router.push({
        name: ROUTE_NAMES.AUTH_LOGIN_USER_SELECT,
        query: {
          reason: 'logOut'
        }
      });
    } else {
      await router.push({
        name: ROUTE_NAMES.AUTH_LOGIN_PASSWORD,
        query: {
          reason: 'logOut'
        }
      });
    }
    await router.push({ name: ROUTE_NAMES.AUTH_LOGIN_PASSWORD });
  }
};

const handleLogout = async () => {
  isOpen.value = false;
  await mutateLogout();
  if (userIndex.value !== null) {
    postAfterLoggedOut(userIndex.value);
  }
  await redirectToLogin();
};

const handleLogoutAll = async () => {
  await mutateLogoutAll();
  await redirectToLogin(false, true);
};
</script>

<template>
  <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions -->
  <div v-if="mobile && isOpen" class="custom-overlay" @click.stop="isOpen = false"></div>
  <v-menu
    v-model="isOpen"
    :close-on-content-click="false"
    :transition="mobile ? undefined : 'slide-y-transition'"
  >
    <template #activator="{ props: menuActivatorProps }">
      <VTooltip :text="isOpen ? t('account.dropdown.close') : t('account.dropdown.open')">
        <template #activator="{ props: tooltipActivatorProp }">
          <filled-btn
            :size="!mobile ? 40 : 32"
            color="on-primary"
            data-test-id="accounts-dropdown-open"
            icon="mdi-account-outline"
            v-bind="{
              ...menuActivatorProps,
              ...tooltipActivatorProp
            }"
          >
            <InitialsAvatar
              :image="avatarData?.avatar"
              :name="currentAccount?.name ?? currentAccount?.email"
              :size="!mobile ? 40 : 32"
              class="initials-btn"
              color="initials-btn-text"
            />
          </filled-btn>
        </template>
      </VTooltip>
    </template>

    <v-card
      :class="{ 'accounts-menu--mobile': mobile }"
      class="accounts-menu"
      data-test-id="accountsDropdown-menu"
    >
      <CloseBtn
        class="accounts-menu__close-btn"
        color="on-surface"
        data-test-id="accountsDropdown-closeBtn"
        icon
        @click="isOpen = false"
      >
        <VIcon>mdi-close</VIcon>
      </CloseBtn>

      <v-list>
        <v-list-item>
          <div class="user-item">
            <InitialsAvatar
              :image="avatarData?.avatar"
              :name="currentAccount?.name ?? currentAccount?.email"
              class="user-item__icon"
              color="initials-btn-text"
            />
            <div class="user-item__content">
              <span class="user-item__title text-body-lg">{{ currentAccount?.name }}</span>
              <span class="user-item__subtitle text-body-md">{{ currentAccount?.email }}</span>
            </div>
          </div>
        </v-list-item>
      </v-list>

      <v-divider></v-divider>

      <v-list class="accounts-menu__list">
        <v-list-item
          v-for="account in filteredAccounts"
          :key="`${account.uid}-${account.email}`"
          class="accounts-menu__list-item"
        >
          <RouterLink
            :to="{ name: ROUTE_NAMES.BASE_LAYOUT, params: { userIndex: account.uid } }"
            :target="attributesData?.openAccountInSameTab ? '_self' : '_blank'"
            class="user-item-link"
            @click="isOpen = false"
          >
            <div class="user-item">
              <InitialsAvatar
                :image="account.avatar"
                :name="account.name"
                class="user-item__avatar"
                color="primary"
              />
              <div class="user-item__content">
                <span v-if="account.name" class="user-item__title text-body-lg">
                  {{ account.name }}
                </span>
                <span :class="account.name ? ['text-body-md'] : ['text-body-lg']">
                  {{ account.email }}
                </span>
              </div>
            </div>
          </RouterLink>
        </v-list-item>
      </v-list>

      <v-card-actions class="accounts-menu__actions">
        <outlined-btn
          class="action-btn__add"
          data-test-id="accounts-dropdown-add-account"
          @click.prevent="redirectToLogin(true)"
          sentry-unmask
        >
          {{ t('account.actions.addAccount') }}
        </outlined-btn>
        <v-btn-group class="action-btn" rounded="md">
          <filled-btn
            class="action-btn__logout"
            data-test-id="accounts-dropdown-logout"
            @click="handleLogout"
            sentry-unmask
            >{{ t('account.actions.logout') }}
          </filled-btn>
          <filled-btn class="action-btn__arrow" min-width="44px">
            <v-menu v-model="showLogoutList" activator="parent" location="bottom">
              <template #activator="{ props }">
                <VIcon v-bind="props">
                  {{ logoutListIcon }}
                </VIcon>
              </template>
              <v-list class="action-btn__menu" sentry-unmask>
                <v-list-item @click="handleLogoutAll" data-test-id="accounts-dropdown-logout-all">
                  <v-list-item-title>
                    <VIcon>mdi-logout</VIcon>
                    {{ t('account.actions.logoutAll') }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </filled-btn>
        </v-btn-group>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<style lang="scss">
@use '../../assets/styles/settings' as settings;
@use 'sass:map';

.accounts-menu {
  padding: 1rem 0;
  max-height: calc(100vh - 3.75rem);
  max-width: calc(100vw - 2rem);

  &--mobile {
    z-index: 1007;
    width: 90vw;
    max-height: 90svh;
    overflow: auto;

    .v-overlay__content:has(> &) {
      top: 40svh !important;
      left: 50% !important;
      transform: translate(-50%, -50%);
    }
  }

  @media screen and #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
    margin-top: 1rem;
  }

  &__close-btn {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;
  }

  &__actions {
    padding: 1rem;
    display: flex;

    // align-items: space-between;

    @media screen and #{map.get(
        settings.$display-breakpoints,
        'sm-and-up'
      )} {
      padding: 1rem 2rem;
    }
  }

  &__list {
    padding: 0;
  }

  &__list-item {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;

    &:hover {
      background: rgb(var(--v-theme-surface-container));
    }

    &:focus-visible,
    &:focus-within {
      background: rgb(var(--v-theme-surface-container));
    }
  }
}

.action-btn {
  height: auto !important;
  border-radius: 0.375rem !important;
  margin-left: 1rem;
  flex: 1 auto;

  &__add,
  &__logout {
    flex: 1 auto;
  }

  &__arrow {
    margin-inline-start: 0.125rem !important;
    padding: 0 0.5rem !important;
    flex: auto;
  }

  &__menu {
    margin-top: 0.5rem;
  }
}

.user-item {
  display: flex;
  align-items: center;

  &__content {
    display: flex;
    flex-direction: column;
    padding-left: 1rem;
  }

  &__title {
    font-weight: 500;
  }
}

.user-item-link {
  text-decoration: none;
  display: block !important;
  color: inherit;
}

.initials-btn {
  border: none;
  background: rgb(var(--v-theme-initials-btn)) !important;
  color: rgb(var(--v-theme-initials-btn-text)) !important;

  &:hover {
    border: 1px solid rgb(var(--v-theme-initials-btn-hover));
  }
}

.custom-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: #000;
  opacity: 0.3;
  z-index: 1006;
}
</style>
